import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { formatDate3 } from '../../../utils/dateFormat';

interface RecordingDialogProps {
    visible: boolean;
    onHide: () => void;
    fileUrl: string;
    currentRowData?: {
        recording_date: string;
        employee_name: string;
        recording_title: string;
        recording_length: string;
    };
}

const RecordingDialog: React.FC<RecordingDialogProps> = ({ visible, onHide, fileUrl, currentRowData }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [duration, setDuration] = useState<string | number>('0:00');

    const togglePlayPause = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }

        setIsPlaying(!isPlaying);
    };

    const onTimeUpdate = () => {
        if (audioRef.current) {
            setProgress(audioRef.current.currentTime);
        }
    };

    const onLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(currentRowData?.recording_length ?? '0:00');
        }
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const handleDialogHide = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        setProgress(0);
        onHide();
    };

    function parseDurationToTime(input: string): string {
        const hrMatch = input?.match(/(\d+)\s*hr/);
        const minMatch = input?.match(/(\d+)\s*min/);
        const secMatch = input?.match(/(\d+)\s*sec/);

        const hours = hrMatch ? parseInt(hrMatch[1], 10) : 0;
        const minutes = minMatch ? parseInt(minMatch[1], 10) : 0;
        const seconds = secMatch ? parseInt(secMatch[1], 10) : 0;

        const pad = (num: number) => num.toString().padStart(2, '0');

        if (hours > 0) {
            return `${hours}:${pad(minutes)}:${pad(seconds)}`;
        } else {
            return `${minutes}:${pad(seconds)}`;
        }
    }

    function parseDurationToSeconds(durationStr: string): number {
        const regex = /(?:(\d+)\s*hr)?\s*(?:(\d+)\s*min)?\s*(?:(\d+)\s*sec)?/i;
        const match = durationStr?.match(regex);

        if (!match) return 0;

        const hours = parseInt(match[1] || '0', 10);
        const minutes = parseInt(match[2] || '0', 10);
        const seconds = parseInt(match[3] || '0', 10);

        return hours * 3600 + minutes * 60 + seconds;
    }

    return (
        <Dialog
            header={currentRowData?.recording_title}
            visible={visible}
            onHide={handleDialogHide}
            style={{ width: '650px', borderRadius: '10px' }}
            closable
            dismissableMask
        >
            <div
                style={{
                    border: '1px solid #1A5AA5',
                    borderRadius: '20px',
                    padding: '1rem',
                    marginTop: '20px',
                    marginBottom: '20px',
                    height: '150px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 500,
                        marginBottom: '1rem',
                    }}
                >
                    <span>
                        {formatDate3(
                            currentRowData?.recording_date
                                ? new Date(currentRowData.recording_date)
                                : new Date()
                        )}
                    </span>
                    <span>{currentRowData?.employee_name}</span>
                </div>

                <div
                    role="button"
                    tabIndex={0}
                    style={{
                        position: 'relative',
                        height: '10px',
                        background: '#d3d3d3',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                    }}
                    onClick={(e) => {
                        if (!audioRef.current) return;
                        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        const width = rect.width;
                        const percent = clickX / width;
                        const totalSeconds = parseDurationToSeconds(String(duration));
                        audioRef.current.currentTime = percent * totalSeconds;
                        setProgress(audioRef.current.currentTime);
                    }}
                    onKeyDown={(e) => {
                        if (!audioRef.current) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                            const totalSeconds = parseDurationToSeconds(String(duration));
                            audioRef.current.currentTime = totalSeconds / 2;
                            setProgress(audioRef.current.currentTime);
                            e.preventDefault();
                        }
                    }}
                >
                    <div
                        style={{
                            width: `${
                                audioRef.current?.duration
                                    ? (progress / parseDurationToSeconds(String(duration))) * 100
                                    : 0
                            }%`,
                            height: '100%',
                            backgroundColor: '#145ea8',
                            borderRadius: '10px',
                            transition: 'width 0.2s',
                        }}
                    />
                </div>

                <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '14px', color: '#333' }}>
                    {formatTime(progress)} / {parseDurationToTime(String(duration))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <Button
                        icon="pi pi-pause"
                        className="p-button-outlined"
                        style={{
                            borderRadius: '10px',
                            borderColor: '#1A5AA5',
                            color: '#1A5AA5',
                            height: '20px',
                            width: '30px',
                        }}
                        disabled={!isPlaying}
                        onClick={togglePlayPause}
                    />
                    <Button
                        icon="pi pi-play"
                        className="p-button-outlined"
                        style={{
                            borderRadius: '10px',
                            borderColor: '#1A5AA5',
                            color: '#1A5AA5',
                            height: '20px',
                            width: '30px',
                        }}
                        disabled={isPlaying}
                        onClick={togglePlayPause}
                    />
                </div>

                <audio
                    ref={audioRef}
                    src={fileUrl}
                    onTimeUpdate={onTimeUpdate}
                    onLoadedMetadata={onLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    hidden
                >
                    <track kind="captions" src={fileUrl} srcLang="en" label="English captions" />
                </audio>
            </div>
            <div style={{ textAlign: 'right' }}>
                <Button
                    label="Close"
                    icon="pi pi-times"
                    className="btn-secondary"
                    // style={{
                    //     borderRadius: '30px',
                    //     color: '#EB419E',
                    //     borderColor: 'linear-gradient(to right, #F069DE, #6a3dfb)',
                    //     height: '45px',
                    // }}
                    onClick={handleDialogHide}
                />
            </div>
        </Dialog>
    );
};

export default RecordingDialog;
